import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { AuthContext } from "../../context/AuthContext";

import sortUp from "../../assets/sort_up.png";
import sortDown from "../../assets/sort_down.png";
import "./DataTable.css";

// Helper function to safely access nested property values from an object.
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

export default function DataTable({
  fetchDataFunction,
  columns,
  optionalColumns,
  sortOptions,
  filterDefinitions,
  defaultSortBy,
  itemKey,
  customRenderers,
  rowActions,
  actionsColumnLabel = "Actions",
  onSelectionChange,
  showCheckboxes = false,
  storageKey,
}) {
  const { token } = useContext(AuthContext);

  // --- STATE MANAGEMENT ---
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState(() => {
    if (!storageKey) return defaultSortBy || sortOptions[0]?.value || "";
    try {
      const saved = localStorage.getItem(`${storageKey}-sortBy`);
      return saved || defaultSortBy || sortOptions[0]?.value || "";
    } catch (e) {
      return defaultSortBy || sortOptions[0]?.value || "";
    }
  });
  const [sortOrder, setSortOrder] = useState(() => {
    if (!storageKey) return "asc";
    try {
      const saved = localStorage.getItem(`${storageKey}-sortOrder`);
      return saved || "asc";
    } catch (e) {
      return "asc";
    }
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(null);

  // State for selected items, using a Set for efficient add/delete/check operations.
  const [selectedItems, setSelectedItems] = useState(new Set());

  const tableContainerRef = useRef(null);
  const prevVisibleOptionalsLength = useRef(0);

  const [lastEditedFilterKey, setLastEditedFilterKey] = useState(null);
  const filterThRefs = useRef({});
  const scrollPositionRef = useRef(0);

  // Generates the initial state for filters based on their definitions.
  const initialFilters = useMemo(
    () =>
      filterDefinitions.reduce((acc, def) => {
        const stateKey = def.filterKey || def.key;

        if (def.type === "minmax" || def.type === "date-range") {
          const minKey = def.minKey || `min_${stateKey}`; // Use custom minKey or default
          const maxKey = def.maxKey || `max_${stateKey}`; // Use custom maxKey or default
          acc[minKey] = "";
          acc[maxKey] = "";
        } else {
          acc[stateKey] = "";
        }
        return acc;
      }, {}),
    [filterDefinitions],
  );

  const [filters, setFilters] = useState(() => {
    if (!storageKey) return initialFilters;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : initialFilters;
    } catch (e) {
      console.error("Failed to load filters from localStorage", e);
      return initialFilters;
    }
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (e) {
      console.error("Failed to save filters to localStorage", e);
    }
  }, [filters, storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`${storageKey}-sortBy`, sortBy);
      localStorage.setItem(`${storageKey}-sortOrder`, sortOrder);
    } catch (e) {
      console.error("Failed to save sort to localStorage", e);
    }
  }, [sortBy, sortOrder, storageKey]);

  const [visibleOptionals, setVisibleOptionals] = useState([]);

  // Combines always-visible and optional columns into a single list for rendering.
  const visibleColumns = useMemo(() => {
    return [
      ...columns,
      ...optionalColumns.filter((col) => visibleOptionals.includes(col.key)),
    ];
  }, [visibleOptionals, columns, optionalColumns]);

  // --- DATA FETCHING EFFECT ---
  useEffect(() => {
    if (!token) return;

    if (tableContainerRef.current) {
      scrollPositionRef.current = tableContainerRef.current.scrollLeft;
    }

    setLoading(true);
    if (showCheckboxes) {
      setSelectedItems(new Set());
    }

    fetchDataFunction({
      token,
      skip: (page - 1) * pageSize,
      limit: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
      filters,
    })
      .then((data) => {
        setItems(data.items || []);
        setTotalRecords(data.total || 0);
        setFilteredRecords(data.filtered !== undefined ? data.filtered : null);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setItems([]);
        setTotalRecords(0);
        setFilteredRecords(null);
      })
      .finally(() => {
        setLoading(false);
        requestAnimationFrame(() => {
          if (tableContainerRef.current) {
            tableContainerRef.current.scrollLeft = scrollPositionRef.current;
          }
        });
      });
  }, [
    token,
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    fetchDataFunction,
    showCheckboxes,
  ]);

  useEffect(() => {
    if (visibleOptionals.length > prevVisibleOptionalsLength.current) {
      const container = tableContainerRef.current;
      if (container) {
        container.scrollTo({
          left: container.scrollWidth,
          behavior: "smooth",
        });
      }
    }
    prevVisibleOptionalsLength.current = visibleOptionals.length;
  }, [visibleOptionals]);

  useEffect(() => {
    if (lastEditedFilterKey && filterThRefs.current[lastEditedFilterKey]) {
      const container = tableContainerRef.current;
      const thElement = filterThRefs.current[lastEditedFilterKey];

      if (container && thElement) {
        const containerRect = container.getBoundingClientRect();
        const thRect = thElement.getBoundingClientRect();

        if (
          thRect.left < containerRect.left ||
          thRect.right > containerRect.right
        ) {
          container.scrollTo({
            left: thElement.offsetLeft - container.offsetLeft,
            behavior: "smooth",
          });
        }
      }
      setLastEditedFilterKey(null);
    }
  }, [lastEditedFilterKey]);

  // Notify parent component when selection changes
  useEffect(() => {
    if (showCheckboxes && onSelectionChange) {
      const selectedIds = Array.from(selectedItems);
      const selectedRows = items.filter((item) =>
        selectedItems.has(getNestedValue(item, itemKey)),
      );
      onSelectionChange(selectedIds, selectedRows); // NEW: Pass both
    }
  }, [selectedItems, items, itemKey, onSelectionChange, showCheckboxes]);

  // --- HANDLER FUNCTIONS ---
  // Handles sorting when a column header is clicked.
  function handleSort(colKey) {
    const apiSortKey =
      sortOptions.find((opt) => opt.value.endsWith(colKey))?.value ||
      `transaction.${colKey}`; // Fallback prefix
    if (sortBy === apiSortKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(apiSortKey);
      setSortOrder("asc");
    }
    setPage(1);
  }

  // Toggles selection for a single item by its key.
  function handleSelectItem(key) {
    setSelectedItems((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(key)) {
        newSelection.delete(key);
      } else {
        newSelection.add(key);
      }
      return newSelection;
    });
  }

  // Selects or deselects all items on the current page.
  function handleSelectAll() {
    const currentPageKeys = items.map((item) => getNestedValue(item, itemKey));
    const allSelectedOnPage =
      currentPageKeys.length > 0 &&
      currentPageKeys.every((key) => selectedItems.has(key));

    setSelectedItems((prev) => {
      const newSelection = new Set(prev);
      if (allSelectedOnPage) {
        currentPageKeys.forEach((key) => newSelection.delete(key));
      } else {
        currentPageKeys.forEach((key) => newSelection.add(key));
      }
      return newSelection;
    });
  }

  // Toggles the visibility of an optional column.
  function toggleColumn(colKey) {
    setVisibleOptionals((prev) =>
      prev.includes(colKey)
        ? prev.filter((c) => c !== colKey)
        : [...prev, colKey],
    );
  }

  // Updates the filter state when a filter input changes.
  function onFilterChange(key, value, columnKey) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    if (columnKey) setLastEditedFilterKey(columnKey);
  }

  // Resets all filters to their initial state.
  function resetFilters() {
    setFilters(initialFilters);
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    setPage(1);
  }

  const allOnPageSelected =
    showCheckboxes &&
    items.length > 0 &&
    items.every((item) => selectedItems.has(getNestedValue(item, itemKey)));
  const totalColumns =
    visibleColumns.length +
    (rowActions && rowActions.length > 0 ? 1 : 0) +
    (showCheckboxes ? 1 : 0);

  // --- JSX RENDER ---
  return (
    <section className="dt-container">
      {/* Toolbar for sorting, stats, and page size */}
      <div className="dt-toolbar">
        <div className="dt-sort">
          <span className="dt-sort-label">Sort by:</span>
          <div className="dt-select-wrapper">
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="dt-select-wrapper">
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setPage(1);
              }}
            >
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>
        </div>
        <div className="dt-stats">
          <span className="dt-stat">Total: {totalRecords}</span>
          {filteredRecords !== null && (
            <>
              <span className="dt-stat-sep"></span>
              <span className="dt-stat">Filtered: {filteredRecords}</span>
            </>
          )}
        </div>
      </div>

      {/* Row for optional column toggles and filter actions */}
      <div className="dt-columns-row">
        <div className="dt-columns">
          {optionalColumns.map((col) => (
            <button
              key={col.key}
              type="button"
              className={`dt-column-toggle-btn ${visibleOptionals.includes(col.key) ? "active" : ""}`}
              onClick={() => toggleColumn(col.key)}
            >
              + {col.label}
            </button>
          ))}
        </div>
        <div className="dt-pagesize">
          <span className="dt-pagesize-label">Rows:</span>
          <div className="dt-select-wrapper">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="dt-filter-actions">
            <button
              type="button"
              className="dt-reset-filters-btn"
              onClick={resetFilters}
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Main data table with horizontal scroll */}
      <div className="dt-table-container" ref={tableContainerRef}>
        <table className="dt-data-table">
          <thead>
            <tr>
              {/* Checkbox in the header to select/deselect all */}
              {showCheckboxes && (
                <th className="dt-checkbox-cell">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={allOnPageSelected}
                    disabled={items.length === 0}
                  />
                </th>
              )}
              {visibleColumns.map((col) => {
                const isSortable =
                  col.key &&
                  sortOptions.some((option) => option.value.endsWith(col.key));
                return (
                  <th
                    key={col.key || `col-${Math.random()}`}
                    onClick={isSortable ? () => handleSort(col.key) : undefined}
                    style={{ cursor: isSortable ? "pointer" : "default" }}
                  >
                    <span className="dt-th-inner">
                      <span className="dt-th-label">{col.label}</span>
                      {isSortable &&
                        sortBy.endsWith(col.key.split(".").pop()) && (
                          <img
                            src={sortOrder === "asc" ? sortUp : sortDown}
                            alt=""
                            className="dt-sort-icon"
                          />
                        )}
                    </span>
                  </th>
                );
              })}
              {rowActions && rowActions.length > 0 && (
                <th className="dt-actions-header">{actionsColumnLabel}</th>
              )}
            </tr>

            <tr className="dt-filter-row">
              {showCheckboxes && <th />}
              {visibleColumns.map((col) => {
                const filterDef = filterDefinitions.find(
                  (f) => f.key === col.key,
                );
                return (
                  <th
                    key={`${col.key}-filter`}
                    ref={(el) => (filterThRefs.current[col.key] = el)}
                  >
                    {filterDef
                      ? (() => {
                          const {
                            key,
                            type,
                            options,
                            inputType = "text",
                            minKey,
                            maxKey,
                            filterKey,
                          } = filterDef;
                          const stateKey = filterKey || key;

                          if (type === "minmax" || type === "date-range") {
                            const finalMinKey = minKey || `min_${stateKey}`;
                            const finalMaxKey = maxKey || `max_${stateKey}`;
                            return (
                              <div className="dt-filter-minmax-group">
                                <input
                                  type={
                                    type === "date-range" ? "date" : inputType
                                  }
                                  placeholder="Min"
                                  value={filters[finalMinKey] || ""}
                                  onChange={(e) =>
                                    onFilterChange(
                                      finalMinKey,
                                      e.target.value,
                                      col.key,
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                                {maxKey !== "" && (
                                  <input
                                    type={
                                      type === "date-range" ? "date" : inputType
                                    }
                                    placeholder="Max"
                                    value={filters[finalMaxKey] || ""}
                                    onChange={(e) =>
                                      onFilterChange(
                                        finalMaxKey,
                                        e.target.value,
                                        col.key,
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </div>
                            );
                          }
                          if (type === "select") {
                            return (
                              <select
                                value={filters[stateKey] || ""}
                                onChange={(e) =>
                                  onFilterChange(
                                    stateKey,
                                    e.target.value,
                                    col.key,
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="">All</option>
                                {options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            );
                          }
                          return (
                            <input
                              type={inputType}
                              value={filters[stateKey] || ""}
                              onChange={(e) =>
                                onFilterChange(
                                  stateKey,
                                  e.target.value,
                                  col.key,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          );
                        })()
                      : null}
                  </th>
                );
              })}
              {rowActions && rowActions.length > 0 && <th />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalColumns}>Loading...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={totalColumns}>No results</td>
              </tr>
            ) : (
              items.map((item) => {
                const key = getNestedValue(item, itemKey);
                const isSelected = showCheckboxes && selectedItems.has(key);
                return (
                  <tr key={key} className={isSelected ? "dt-row-selected" : ""}>
                    {/* Checkbox for each row */}
                    {showCheckboxes && (
                      <td className="dt-checkbox-cell">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectItem(key)}
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => {
                      if (customRenderers && customRenderers[col.key]) {
                        return (
                          <td key={col.key}>
                            {customRenderers[col.key](item)}
                          </td>
                        );
                      }
                      const val = getNestedValue(item, col.key);
                      return (
                        <td key={col.key}>
                          {val === null || val === undefined || val === ""
                            ? "-"
                            : String(val)}
                        </td>
                      );
                    })}
                    {rowActions && rowActions.length > 0 && (
                      <td className="dt-actions-cell">
                        <div className="dt-actions-container">
                          {rowActions.map((action) => (
                            <button
                              key={action.label}
                              className="dt-action-btn"
                              onClick={() => action.onClick(item)}
                              title={action.label}
                            >
                              <img src={action.icon} alt={action.label} />
                            </button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="dt-pagination-bar">
        <button
          className="dt-pagination-btn"
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>
        <span className="dt-pagination-center">Page {page}</span>
        <button
          className="dt-pagination-btn"
          disabled={items.length < pageSize}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </section>
  );
}

DataTable.propTypes = {
  fetchDataFunction: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  optionalColumns: PropTypes.array.isRequired,
  sortOptions: PropTypes.array.isRequired,
  filterDefinitions: PropTypes.array.isRequired,
  defaultSortBy: PropTypes.string,
  itemKey: PropTypes.string.isRequired,
  customRenderers: PropTypes.object,
  rowActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  actionsColumnLabel: PropTypes.string,
  onSelectionChange: PropTypes.func,
  showCheckboxes: PropTypes.bool,
  storageKey: PropTypes.string,
};
